import React, { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Plus, Edit, Trash2 } from 'lucide-react'
import { useEngineScoreLabels, useCreateEngineScoreLabel, useUpdateEngineScoreLabel, useDeleteEngineScoreLabel } from '../hooks'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'

interface ScoreLabel {
  value: number
  label: string
  description: string
  created_at: string
  updated_at: string
}

const ScoreLabelManagement: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState<ScoreLabel | null>(null)
  const [formData, setFormData] = useState({
    value: 1,
    label: '',
    description: ''
  })



  // Fetch score labels
  const { data: labelsData, isLoading, error } = useEngineScoreLabels()

  // Mutations via hooks
  const createLabelMutation = useCreateEngineScoreLabel()
  const updateLabelMutation = useUpdateEngineScoreLabel()
  const deleteLabelMutation = useDeleteEngineScoreLabel()

  const resetForm = () => {
    setFormData({
      value: 1,
      label: '',
      description: ''
    })
  }

  const handleCreate = () => {
    createLabelMutation.mutate(formData)
  }

  const handleEdit = (label: ScoreLabel) => {
    setSelectedLabel(label)
    setFormData({
      value: label.value,
      label: label.label,
      description: label.description
    })
    setIsEditDialogOpen(true)
  }

  const handleUpdate = () => {
    if (selectedLabel) {
      updateLabelMutation.mutate({
        value: selectedLabel.value,
        data: formData
      })
    }
  }

  const handleDelete = (value: number) => {
    deleteLabelMutation.mutate(value)
  }

  const listState = useListState({ defaultPerPage: 50 })

  const labels = labelsData?.data || []

  // Client-side search filtering
  const filteredLabels = useMemo(() => {
    const term = listState.search.toLowerCase()
    if (!term) return labels
    return labels.filter((l: ScoreLabel) =>
      l.label.toLowerCase().includes(term) ||
      l.description?.toLowerCase().includes(term)
    )
  }, [labels, listState.search])

  if (isLoading) return <div>Loading...</div>
  if (error) return <div>Error loading score labels</div>

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Score Label Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage score label definitions for assessment answers
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AuthorizationGate actions={['export_scoring_labels', 'import_scoring_labels']} mode="any">
            <ImportExportButtons
              entityType="score_labels"
              entityLabel="Score Labels"
            />
          </AuthorizationGate>
          <AuthorizationGate actions={['create_scoring_label']}>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Label
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Score Label</DialogTitle>
                  <DialogDescription>
                    Add a new score label to the system
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="value">Score Value</Label>
                    <Input
                      id="value"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="label">Label</Label>
                    <Input
                      id="label"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder="e.g., Not Implemented, Excellence"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Description of this score level..."
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createLabelMutation.isPending}>
                    {createLabelMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </AuthorizationGate>
        </div>
      </div>

      {/* Search Toolbar */}
      <DataToolbar
        listState={listState}
        searchPlaceholder="Search score labels..."
        totalItems={filteredLabels.length}
        showPerPage={false}
      />

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Value</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLabels.map((label: ScoreLabel) => (
                <TableRow key={label.value}>
                  <TableCell className="font-mono font-medium">{label.value}</TableCell>
                  <TableCell className="font-medium">{label.label}</TableCell>
                  <TableCell className="max-w-xs truncate">{label.description}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <AuthorizationGate actions={['update_scoring_label']}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(label)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </AuthorizationGate>
                      <AuthorizationGate actions={['delete_scoring_label']}>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Score Label</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete score label {label.value} "{label.label}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(label.value)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </AuthorizationGate>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Score Label</DialogTitle>
            <DialogDescription>
              Update the score label information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-label">Label</Label>
              <Input
                id="edit-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateLabelMutation.isPending}>
              {updateLabelMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

export default ScoreLabelManagement
